import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { APIService } from 'api/APIService'
import { getIsAuthenticated } from 'src/store/app.state.store'
import { unsetAppointment } from 'src/store/appointment.store'
import type { RootState } from 'src/store'
import { Modal } from 'components/Utility/Modal/Modal'
import { Loader } from 'components/Utility/Loader/Loader'
import { StatusModal } from 'components/Utility/StatusModal/StatusModal'
import { QuickSignUpController } from 'components/Forms/QuickSignUp/QuickSignUpController'
import { AppointmentFormController } from './AppointmentForm/AppointmentFormController'
import { AppointmentConfirm } from './AppointmentConfirm/AppointmentConfirm'
import styles from './Appointment.module.scss'

const METRIKA_COUNTER = 21520873

type Stage = 'form' | 'confirm' | 'signup' | 'submitting' | 'success'

/** #appointment: form, confirmation, sign-up for guests, then sending the booking */
export const AppointmentController: React.FC = React.memo(() => {
  const [stage, setStage] = useState<Stage>('form')
  const appointment = useSelector((state: RootState) => state.appointment)
  const { id, salonId, services, date, time, comment, notification } = appointment
  const isAuth = useSelector(getIsAuthenticated)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const quickSignUpToken = useRef<string | null>(null)
  const submitting = useRef(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const setFormStage = useCallback(() => setStage('form'), [])
  const setConfirmStage = useCallback(() => setStage('confirm'), [])

  const submit = useCallback(async () => {
    if (submitting.current) {
      return
    }
    submitting.current = true
    setStage('submitting')
    try {
      const request = {
        salon: salonId as number,
        services,
        date: date as string,
        time: time as string,
        comment: (comment ?? '').trim(),
        notification,
      }
      let result: unknown
      if (id) {
        await APIService.appointment.update(id, request)
      } else {
        result = await APIService.appointment.create(request, quickSignUpToken.current)
      }
      setStage('success')
      dispatch(unsetAppointment())
      quickSignUpToken.current = null
      if (window.ym && Array.isArray(result)) {
        window.ym(METRIKA_COUNTER, 'reachGoal', 'record', { appointment_id: result[0] })
      }
    } catch (e) {
      setSubmitError((e as Error).message)
    } finally {
      submitting.current = false
    }
  }, [salonId, services, date, time, comment, id, notification, dispatch])

  const handleConfirm = useCallback(() => {
    if (!isAuth) {
      setStage('signup')
    } else {
      submit()
    }
  }, [isAuth, submit])

  const handleQuickSignUp = useCallback((token: string) => {
    quickSignUpToken.current = token
    submit()
  }, [submit])

  const handleFinish = useCallback(() => {
    navigate(isAuth ? '#profile' : '#', { replace: true })
  }, [isAuth, navigate])

  const dismissAppointment = useCallback(() => {
    navigate('#', { replace: true })
  }, [navigate])

  const dismissError = useCallback(() => {
    setSubmitError(null)
    setStage('form')
  }, [])

  useEffect(() => () => {
    submitting.current = false
  }, [])

  if (submitError !== null) {
    return (
      <StatusModal
        isOpen
        modalText={submitError}
        status={'error'}
        buttonText={'ОК'}
        onClose={dismissError}
        onButtonClick={dismissError}
      />
    )
  }

  switch (stage) {
    case 'confirm':
      return (
        <Modal
          isOpen
          back={setFormStage}
          onClose={setFormStage}
          title={<>Проверьте, пожалуйста, <br /> данные записи</>}
        >
          <AppointmentConfirm onSubmit={handleConfirm} />
        </Modal>
      )
    case 'signup':
      return <QuickSignUpController onSubmit={handleQuickSignUp} onCancel={setConfirmStage} />
    case 'submitting':
      return (
        <Modal isOpen className={styles.appointmentSubmittingModal}>
          <Loader />
        </Modal>
      )
    case 'success':
      return (
        <StatusModal
          isOpen
          status={'success'}
          modalText={'Запись успешно создана'}
          buttonText={'ОК'}
          onButtonClick={handleFinish}
          onClose={dismissAppointment}
        />
      )
    default:
      return <AppointmentFormController onSubmit={setConfirmStage} />
  }
})
AppointmentController.displayName = 'AppointmentController'
